import React, { useEffect, useState } from "react";
import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Grid,
    Paper,
    FormHelperText
} from '@material-ui/core';
import instance from "api/globalaxios";
import FormError from "./FormError";

// Dynamically generate years from current year down to 2012
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = ['Select'];
for (let year = CURRENT_YEAR; year >= 2012; year--) {
    YEARS.push(year.toString());
}

const PREFIX_OPTIONS = ['AC', 'IAC', 'CMP', 'MSEFC'];

const NO_YEAR_TYPES = ['CMP', 'MSEFC'];

// `initialValues` ({ prefix, number, year }) pre-fills the form and runs the
// search straight away, for callers that arrive with a case already chosen.
// `buttonLabel` names the button, for callers where a match does more than
// load results (e.g. "Open Case").
const SearchCaseForm = ({ classes, handleSearchSuccess, handleSearchError, initialValues, buttonLabel = 'Search' }) => {
    const [localFormData, setLocalFormData] = useState({
        prefix: 'AC',
        number: '',
        year: CURRENT_YEAR.toString()
    });

    const [errors, setErrors] = useState({
        number: '',
        year: ''
    });

    const [loading, setLoading] = useState(false);

    // Error text shown in the ribbon under the Search button (case not found,
    // network failure, etc.).
    const [searchError, setSearchError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchError('');
        if (name === 'prefix') {
            // Switching type changes the input shape (Year vs no Year, numeric vs
            // free text), so clear the fields and errors to avoid stale values.
            setErrors({ number: '', year: '' });
            setLocalFormData({ prefix: value, number: '', year: CURRENT_YEAR.toString() });
            return;
        }
        setLocalFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleGoClick = async (formData = localFormData) => {
        const isCmpMsefc = NO_YEAR_TYPES.includes(formData.prefix);
        let hasError = false;
        let newErrors = { number: '', year: '' };
        setSearchError('');

        if (!formData.number) {
            newErrors.number = 'Number is required';
            hasError = true;
        }

        // Year is only relevant for AC / IAC case-number lookups.
        if (!isCmpMsefc && (!formData.year || formData.year === 'Select')) {
            newErrors.year = 'Year is required';
            hasError = true;
        }

        setErrors(newErrors);

        if (!hasError) {
            // AC / IAC: looked up by the full case number ("AC No. 464/2026").
            // CMP / MSEFC: looked up by the registration number entered as-is
            // (e.g. "293/2019") via search_type, with no year appended.
            const searchType = isCmpMsefc ? formData.prefix.toLowerCase() : 'ac';
            const queryValue = isCmpMsefc
                ? formData.number.trim()
                : `${formData.prefix} No. ${formData.number}/${formData.year}`;

            try {
                setLoading(true);

                // Axios handles status codes and parsing automatically
                // suppressGlobalErrorHandling: a failed lookup is shown in the
                // ribbon below, not via the global /fallback redirect or toast.
                const response = await instance.get(
                    `/bac/get-case-by-number/?case_number=${encodeURIComponent(queryValue)}&search_type=${searchType}`,
                    { suppressGlobalErrorHandling: true }
                );

                // Axios puts the successfully parsed payload inside 'response.data'.
                // The second argument is the real case number for callers that need
                // to query other APIs by case number — for a CMP/MSEFC lookup that
                // is the matched case's own number, not the CMP/MSEFC number.
                if (handleSearchSuccess) {
                    const resolvedCaseNumber = isCmpMsefc
                        ? (response.data?.tiac_custom_dispute_id || queryValue)
                        : queryValue;
                    handleSearchSuccess(response.data, resolvedCaseNumber);
                }

            } catch (error) {
                console.error("API Call Failed:", error);
                const data = error?.response?.data;
                const label = isCmpMsefc
                    ? `${formData.prefix} No. ${formData.number}`
                    : queryValue;
                setSearchError(
                    data?.message ||
                    data?.error ||
                    data?.detail ||
                    (error?.response?.status === 404
                        ? `Case ${label} was not found. Please check the type and number.`
                        : "Case search failed. Please try again.")
                );
                if (handleSearchError) handleSearchError(error);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!initialValues?.number) return;
        const prefilled = {
            prefix: initialValues.prefix || 'AC',
            number: String(initialValues.number),
            year: String(initialValues.year || CURRENT_YEAR),
        };
        setLocalFormData(prefilled);
        handleGoClick(prefilled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues?.prefix, initialValues?.number, initialValues?.year]);

    const isCmpMsefc = NO_YEAR_TYPES.includes(localFormData.prefix);

    return (
        <Paper className={classes?.paper || ''} style={{ padding: 16 }}>
            {/* One field per row on phones: three to a row left each about 66px,
                truncating the Year to "S…" and wrapping "Case Number". */}
            <Grid container spacing={2}>
                {/* 1. Drop-down with values AC and IAC */}
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Type</InputLabel>
                        <Select
                            label="Type"
                            name="prefix"
                            value={localFormData.prefix}
                            onChange={handleInputChange}
                        >
                            {PREFIX_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* 2. Number field. CMP/MSEFC numbers contain a "/", so those
                    types use a free-text field and take the whole width the Year
                    dropdown would otherwise occupy. */}
                <Grid item xs={12} sm={isCmpMsefc ? 8 : 4}>
                    <TextField
                        label={isCmpMsefc ? `${localFormData.prefix} Number` : "Case Number"}
                        name="number"
                        type={isCmpMsefc ? "text" : "number"}
                        placeholder={isCmpMsefc ? "e.g. 293/2019" : ""}
                        fullWidth
                        variant="outlined"
                        value={localFormData.number}
                        onChange={handleInputChange}
                        error={!!errors.number}
                        helperText={errors.number}
                    />
                </Grid>

                {/* 3. Year — only for AC / IAC case-number lookups. */}
                {!isCmpMsefc && (
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" error={!!errors.year}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                label="Year"
                                name="year"
                                value={localFormData.year}
                                onChange={handleInputChange}
                            >
                                {YEARS.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
                        </FormControl>
                    </Grid>
                )}
            </Grid>

            {/* Search Action Button */}
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 18 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                    className={classes?.button || ''}
                    onClick={() => handleGoClick()}
                >
                    {loading ? 'Searching...' : buttonLabel}
                </Button>
            </Grid>

            {/* Search failure strip, shared with the rest of the ACCB forms. */}
            <FormError message={searchError} style={{ marginTop: 16 }} />
        </Paper>
    );
};

export default SearchCaseForm;